import React, { useState, useEffect } from "react";
import { Box, Flex, Grid, Image, Badge, Label } from "theme-ui";
import Separator from "components/ui/Separator";
import H3 from "components/typography/H3";
import { useToast } from "components/toast";
import BasicButton from "components/forms/BasicButton";
import { Spinner } from "theme-ui";
import { ZohoBooksLogo } from "components/svg";
import H5 from "components/typography/H5";
import { zohoInitAuth, getZohoIntegration, setZohoAutoSync } from "api/Integrations";
import { resyncZohoClients, resyncProviders, resyncProjects, resyncProductsAndServices, resyncPurchaseOrders, resyncInvoices } from "api/Integrations";
import { themevals } from "theme/themevals";
import P from "components/typography/P";
import Switch from "react-switch";

const Integrations = ({
  company,
  values,
  errors,
  submitCount,
  setFieldValue,
  initCompany,
}: {
  company: any;
  values: any;
  errors: any;
  submitCount: number;
  setFieldValue: (i: string, v: any) => void;
  initCompany: () => void;
}) => {
  const [isConnecting, setIsConnecting] = useState(false);
  const [isZohoConnected, setIsZohoConnected] = useState(true);
  const [autoSyncEnabled, setAutoSyncEnabled] = useState(false);
  const [integrationId, setIntegrationId] = useState<string | null>(null);
  const [loadingIntegration, setLoadingIntegration] = useState(true);

  const toast = useToast();

  useEffect(() => {
    const checkIntegration = async () => {
      if (company?.id) {
        const integration = await getZohoIntegration(company.id);
        setIsZohoConnected(!!integration?.id);
        setIntegrationId(integration?.id || null);
        setAutoSyncEnabled(!!integration?.autoSyncOn);
      }
      setLoadingIntegration(false);
    };
    checkIntegration();
  }, []);

  const onToggleAutoSync = async (checked: boolean) => {
    setAutoSyncEnabled(checked);
    if (isZohoConnected && integrationId) {
      try {
        const res = await setZohoAutoSync(integrationId, checked);
        if (res?.id || res?.integration?.id) {
          toast.add(checked ? "Auto Sync turned on." : "Auto Sync turned off.", "var(--theme-ui-colors-success)");
        } else {
          toast.add("Failed to update Auto Sync.", "var(--theme-ui-colors-danger)");
        }
      } catch (error) {
        toast.add("Failed to update Auto Sync.", "var(--theme-ui-colors-danger)");
      }
    }
  };

  const handleZohoIntegration = async () => {
    setIsConnecting(true);
    try {
      const response = await zohoInitAuth(company.id);
      if (response.url) {
        window.location.href = response.url; // Direct redirect
      }
    } catch (error) {
      console.error("Error initiating Zoho integration:", error);
      toast.add("Failed to connect to Zoho", "var(--theme-ui-colors-danger)");
    } finally {
      setIsConnecting(false);
    }
  };

  const _resyncZohoClients = async () => {
    try {
      const res = await resyncZohoClients(company.id);
      if (res?.success) {
        toast.add("Zoho clients resynced successfully.", "var(--theme-ui-colors-success)");
      } else {
        toast.add("Failed to resync Zoho clients.", "var(--theme-ui-colors-danger)");
      }
    } catch (error) {
      console.error("Error resyncing Zoho clients:", error);
      toast.add("Error resyncing Zoho clients.", "var(--theme-ui-colors-danger)");
    }
  };

  const _resyncProviders = async () => {
    try {
      const res = await resyncProviders();
      if (res?.success) {
        toast.add("Zoho providers resynced successfully.", "var(--theme-ui-colors-success)");
      } else {
        toast.add("Failed to resync Zoho providers.", "var(--theme-ui-colors-danger)");
      }
    } catch (error) {
      console.error("Error resyncing Zoho providers:", error);
      toast.add("Error resyncing Zoho providers.", "var(--theme-ui-colors-danger)");
    }
  };

  const _resyncProjects = async () => {
    try {
      const res = await resyncProjects();
      if (res?.success) {
        toast.add("Zoho projects resynced successfully.", "var(--theme-ui-colors-success)");
      } else {
        toast.add("Failed to resync Zoho projects.", "var(--theme-ui-colors-danger)");
      }
    } catch (error) {
      console.error("Error resyncing Zoho projects:", error);
      toast.add("Error resyncing Zoho projects.", "var(--theme-ui-colors-danger)");
    }
  };

  const _resyncProductsAndServices = async () => {
    try {
      const res = await resyncProductsAndServices();
      if (res?.success) {
        toast.add("Zoho items resynced successfully.", "var(--theme-ui-colors-success)");
      } else {
        toast.add("Failed to resync Zoho items.", "var(--theme-ui-colors-danger)");
      }
    } catch (error) {
      console.error("Error resyncing Zoho items:", error);
      toast.add("Error resyncing Zoho items.", "var(--theme-ui-colors-danger)");
    }
  };

  const _resyncPurchaseOrders = async () => {
    try {
      const res = await resyncPurchaseOrders();
      if (res?.success) {
        toast.add("Zoho PO's resynced successfully.", "var(--theme-ui-colors-success)");
      } else {
        toast.add("Failed to resync Zoho PO's.", "var(--theme-ui-colors-danger)");
      }
    } catch (error) {
      console.error("Error resyncing Zoho PO's:", error);
      toast.add("Error resyncing Zoho PO's.", "var(--theme-ui-colors-danger)");
    }
  };

  const _resyncInvoices = async () => {
    try {
      const res = await resyncInvoices({ dryRun: true });
      if (res?.success) {
        const matched = typeof res?.matched === "number" ? res.matched : 0;
        const total = typeof res?.total === "number" ? res.total : 0;
        const message = res?.dryRun ? `Zoho invoices dry-run complete. Matched ${matched}/${total}.` : "Zoho invoices resynced successfully.";
        toast.add(message, "var(--theme-ui-colors-success)");
      } else {
        toast.add("Failed to resync Zoho invoices.", "var(--theme-ui-colors-danger)");
      }
    } catch (error) {
      console.error("Error resyncing Zoho invoices:", error);
      toast.add("Error resyncing Zoho invoices.", "var(--theme-ui-colors-danger)");
    }
  };

  const _resyncInvoicesLive = async () => {
    try {
      const res = await resyncInvoices({ dryRun: false });
      if (res?.success) {
        const matched = typeof res?.matched === "number" ? res.matched : 0;
        const total = typeof res?.total === "number" ? res.total : 0;
        toast.add(`Zoho invoices synced. Updated ${matched}/${total}.`, "var(--theme-ui-colors-success)");
      } else {
        toast.add("Failed to sync Zoho invoices.", "var(--theme-ui-colors-danger)");
      }
    } catch (error) {
      console.error("Error syncing Zoho invoices:", error);
      toast.add("Error syncing Zoho invoices.", "var(--theme-ui-colors-danger)");
    }
  };

  return (
    <>
      <Box sx={{ paddingLeft: "30px", paddingRight: "30px" }}>
        <H3 marginTop={"10px"} marginBottom={0} fontSize={"16px"} fontWeight={"600"} fontFamily={"Poppins"}>
          Integrations
        </H3>
      </Box>
      <Separator />
      <Flex sx={{ paddingLeft: "30px", paddingRight: "30px", gap: "20px", marginBottom: "40px" }}>
        <ZohoBooksLogo width="100px" />
      </Flex>
      <Flex sx={{ flexDirection: "column", paddingLeft: "30px", paddingRight: "30px", marginBottom: "20px" }}>
        <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Status</Label>
        <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
          <Badge variant={isZohoConnected ? "success" : "danger"}>{isZohoConnected ? "Connected" : "Not Connected"}</Badge>
        </P>
      </Flex>
      {isZohoConnected && (
        <Flex sx={{ paddingLeft: "30px", paddingRight: "30px", marginBottom: "60px", flexDirection: "column" }}>
          <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey, marginRight: "12px", marginBottom: "5px" }}>Auto Sync</Label>

          <Switch
            checked={autoSyncEnabled}
            onChange={onToggleAutoSync}
            onColor={themevals.colors.success}
            offColor="#ccc"
            uncheckedIcon={false}
            checkedIcon={false}
            height={15}
            width={26}
            handleDiameter={13}
            disabled={loadingIntegration}
          />
        </Flex>
      )}
      <Flex sx={{ paddingLeft: "30px", paddingRight: "30px", gap: "20px", flexWrap: "wrap" }}>
        {!isZohoConnected && (
          <BasicButton type="button" scheme="primary" onClick={handleZohoIntegration} $submitting={isConnecting} disabled={isConnecting} keepText={true}>
            {isConnecting ? "Connecting..." : "Connect to Zoho Books"}
          </BasicButton>
        )}
        <BasicButton type="button" scheme="primary" onClick={_resyncZohoClients} $submitting={isConnecting} disabled={isConnecting} keepText={true}>
          {"Resync Zoho Clients"}
        </BasicButton>
        <BasicButton type="button" scheme="primary" onClick={_resyncProviders} $submitting={isConnecting} disabled={isConnecting} keepText={true}>
          {"Resync Zoho Providers"}
        </BasicButton>
        <BasicButton type="button" scheme="primary" onClick={_resyncProjects} $submitting={isConnecting} disabled={isConnecting} keepText={true}>
          {"Resync Zoho Projects"}
        </BasicButton>
        <BasicButton type="button" scheme="primary" onClick={_resyncProductsAndServices} $submitting={isConnecting} disabled={isConnecting} keepText={true}>
          {"Resync Zoho Items"}
        </BasicButton>
        <BasicButton type="button" scheme="primary" onClick={_resyncPurchaseOrders} $submitting={isConnecting} disabled={isConnecting} keepText={true}>
          {"Resync Zoho PO's"}
        </BasicButton>
        <BasicButton type="button" scheme="primary" onClick={_resyncInvoices} $submitting={isConnecting} disabled={isConnecting} keepText={true}>
          {"Dry Run Zoho Invoices Resync"}
        </BasicButton>
        <BasicButton type="button" scheme="primary" onClick={_resyncInvoicesLive} $submitting={isConnecting} disabled={isConnecting} keepText={true}>
          {"Resync Zoho Invoices"}
        </BasicButton>
      </Flex>
    </>
  );
};

export default Integrations;
